<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import api from '@/api'
import LoadingIcon from '@/components/LoadingIcon.vue'
import LeadAvatar from '@/components/LeadAvatar.vue'

type SmsNumber = {
  id: number
  name: string | null
  phone_number: string
  phone_number_formatted: string
}

type LeadSearchResult = {
  id: number
  text: string
  name: string
}

const emit = defineEmits<{
  back: []
  conversationCreated: [conversationId: number]
}>()

// Steps: 'search' -> 'number' -> 'message'
const step = ref<'search' | 'number' | 'message'>('search')

// Lead search
const searchQuery = ref('')
const searchResults = ref<LeadSearchResult[]>([])
const searchLoading = ref(false)
const selectedLead = ref<LeadSearchResult | null>(null)

// Phone numbers
const smsNumbers = ref<SmsNumber[]>([])
const numberConversations = ref<Record<number, number>>({})
const numbersLoading = ref(false)
const selectedNumber = ref<SmsNumber | null>(null)

// Message
const message = ref('')
const sending = ref(false)
const error = ref<string | null>(null)

let searchTimeout: ReturnType<typeof setTimeout> | null = null

const searchLeads = async (query: string) => {
  searchLoading.value = true
  try {
    const response = await api.get('/api/v1/leads/search', {
      params: { term: query },
    })
    searchResults.value = response.data.results || []
  } catch (err) {
    console.error('Failed to search leads:', err)
    searchResults.value = []
  } finally {
    searchLoading.value = false
  }
}

onMounted(() => {
  searchLeads('')
})

onBeforeUnmount(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})

watch(searchQuery, (query) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    searchLeads(query)
  }, 300)
})

const selectLead = async (lead: LeadSearchResult) => {
  selectedLead.value = lead

  // Clear previous number data before fetching new
  smsNumbers.value = []
  numberConversations.value = {}
  selectedNumber.value = null

  // Fetch available SMS numbers for this lead
  numbersLoading.value = true
  try {
    const response = await api.get(`/api/v1/leads/${lead.id}/conversation`)

    // Check if this is still the selected lead (user might have clicked another)
    if (selectedLead.value?.id !== lead.id) {
      return // Stale response, ignore
    }

    smsNumbers.value = response.data.numbers || []
    numberConversations.value = response.data.number_conversations || {}

    if (smsNumbers.value.length === 1) {
      // Auto-select if only one number
      selectNumber(smsNumbers.value[0])
    } else if (smsNumbers.value.length > 1) {
      step.value = 'number'
    } else {
      error.value = 'No SMS numbers available'
    }
  } catch (err) {
    // Only show error if this is still the selected lead
    if (selectedLead.value?.id === lead.id) {
      console.error('Failed to fetch SMS numbers:', err)
      error.value = 'Failed to load SMS numbers'
    }
  } finally {
    // Only update loading state if this is still the selected lead
    if (selectedLead.value?.id === lead.id) {
      numbersLoading.value = false
    }
  }
}

const selectNumber = (num: SmsNumber) => {
  selectedNumber.value = num

  // Check if there's already a conversation for this number
  const existingConversationId = numberConversations.value[num.id]
  if (existingConversationId) {
    // Open existing conversation
    emit('conversationCreated', existingConversationId)
  } else {
    step.value = 'message'
  }
}

const sendMessage = async () => {
  if (!selectedLead.value || !selectedNumber.value || !message.value.trim()) return

  sending.value = true
  error.value = null

  try {
    const response = await api.post(`/api/v1/leads/${selectedLead.value.id}/conversation`, {
      number_id: selectedNumber.value.id,
      message: message.value.trim(),
    })

    // Get the conversation ID from response (handle various response structures)
    const conversationId =
      response.data.conversation?.id || response.data.data?.id || response.data.id
    emit('conversationCreated', conversationId)
  } catch (err: unknown) {
    console.error('Failed to create conversation:', err)
    const errorResponse = err as { response?: { data?: { message?: string } } }
    error.value = errorResponse.response?.data?.message || 'Failed to send message'
  } finally {
    sending.value = false
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

const goBack = () => {
  if (step.value === 'message') {
    if (smsNumbers.value.length > 1) {
      step.value = 'number'
      selectedNumber.value = null
    } else {
      step.value = 'search'
      selectedLead.value = null
      selectedNumber.value = null
      smsNumbers.value = []
      numberConversations.value = {}
      searchLeads(searchQuery.value)
    }
  } else if (step.value === 'number') {
    step.value = 'search'
    selectedLead.value = null
    selectedNumber.value = null
    smsNumbers.value = []
    numberConversations.value = {}
    searchLeads(searchQuery.value)
  } else {
    emit('back')
  }
}

const headerTitle = computed(() => {
  switch (step.value) {
    case 'search':
      return 'New Conversation'
    case 'number':
      return 'Select Number'
    case 'message':
      return 'New Message'
    default:
      return 'New Conversation'
  }
})
</script>

<template>
  <div class="bg-white h-[475px] flex flex-col overflow-hidden">
    <div class="flex items-center px-[16px] py-[8px] border-b border-[#eee] bg-white">
      <button
        data-testid="back-button"
        class="bg-transparent border-none p-[4px] cursor-pointer flex items-center justify-center"
        @click="goBack"
      >
        <i class="material-icons text-[24px] text-[#073b4c]">arrow_back</i>
      </button>
      <div data-testid="header-title" class="font-semibold text-[16px] text-[#073b4c] ml-[12px]">
        {{ headerTitle }}
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <!-- Step 1: Lead Search -->
      <div
        v-if="step === 'search'"
        data-testid="search-step"
        class="p-[16px] h-full flex flex-col overflow-hidden"
      >
        <div
          class="flex items-center border border-[#ddd] rounded-[24px] px-[16px] py-[8px] bg-[#f5f5f5] shrink-0"
        >
          <i class="material-icons text-[#808080] text-[20px]! mr-[8px]">search</i>
          <input
            v-model="searchQuery"
            type="text"
            data-testid="search-input"
            class="flex-1 border-none bg-transparent text-[14px] outline-none"
            placeholder="Search for a lead..."
            autofocus
          />
          <i
            v-if="searchQuery"
            data-testid="clear-icon"
            class="material-icons text-[#808080] text-[18px]! cursor-pointer ml-[8px]"
            @click="searchQuery = ''"
            >close</i
          >
        </div>

        <div v-if="searchLoading" class="flex justify-center p-[32px]">
          <LoadingIcon />
        </div>

        <div v-else-if="searchResults.length > 0" class="mt-[16px] flex-1 overflow-y-auto">
          <div
            v-for="lead in searchResults"
            :key="lead.id"
            data-testid="lead-item"
            class="flex items-center p-[12px] rounded-[8px] cursor-pointer transition-[background-color] duration-200 hover:bg-[#f5f5f5]"
            @click="selectLead(lead)"
          >
            <LeadAvatar :name="lead.name" size="md" />
            <div class="ml-[12px]">
              <div class="font-medium text-[#073b4c]">{{ lead.name }}</div>
              <div class="text-[12px] text-[#808080]">{{ lead.text.split(' | ')[1] }}</div>
            </div>
          </div>
        </div>

        <div
          v-else-if="!searchLoading && searchResults.length === 0"
          class="text-center text-[#808080] p-[32px]"
        >
          <p>No leads found</p>
        </div>
      </div>

      <!-- Step 2: Number Selection -->
      <div v-if="step === 'number'" data-testid="number-step" class="p-[16px]">
        <div v-if="numbersLoading" class="flex justify-center p-[32px]">
          <LoadingIcon />
        </div>

        <div v-else class="flex flex-col gap-[8px]">
          <p class="text-[#666] mb-[16px]">Select a number to send from:</p>
          <div
            v-for="num in smsNumbers"
            :key="num.id"
            data-testid="number-item"
            class="flex items-center p-[16px] border border-[#eee] rounded-[8px] cursor-pointer transition-all duration-200 hover:bg-[#f5f5f5] hover:border-[#32a8d9]"
            @click="selectNumber(num)"
          >
            <i class="material-icons text-[#32a8d9] text-[24px] mr-[12px]">phone</i>
            <div class="flex-1">
              <div v-if="num.name" class="font-medium text-[#073b4c]">{{ num.name }}</div>
              <div class="text-[12px] text-[#808080]">{{ num.phone_number_formatted }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Message -->
      <div v-if="step === 'message'" data-testid="message-step" class="flex flex-col h-full">
        <div class="flex items-center p-[16px] border-b border-[#eee]">
          <LeadAvatar :name="selectedLead?.name" size="md" />
          <div class="ml-[12px]">
            <div class="font-semibold text-[#073b4c]">{{ selectedLead?.name }}</div>
            <div class="text-[12px] text-[#808080]">
              From: {{ selectedNumber?.phone_number_formatted }}
            </div>
          </div>
        </div>

        <div
          v-if="error"
          data-testid="error-message"
          class="flex items-center px-[16px] py-[12px] bg-[#ffebee] text-[#ef5350] text-[14px]"
        >
          <i class="material-icons text-[18px]! mr-[8px]">error_outline</i>
          <span>{{ error }}</span>
        </div>

        <div class="flex items-end p-[16px] mt-auto border-t border-[#eee]">
          <textarea
            v-model="message"
            data-testid="message-input"
            class="flex-1 border border-[#ddd] rounded-[18px] px-[16px] py-[12px] text-[14px] resize-none outline-none focus:border-[#32a8d9]"
            placeholder="Type your message..."
            rows="1"
            autofocus
            @keydown="handleKeydown"
          />
          <button
            data-testid="send-button"
            class="bg-[#32a8d9] border-none rounded-full w-[40px] h-[40px] ml-[8px] cursor-pointer flex items-center justify-center shrink-0 transition-[background-color] duration-200 enabled:hover:bg-[#2899c7] disabled:bg-[#ccc] disabled:cursor-not-allowed"
            :disabled="!message.trim() || sending"
            @mousedown.prevent
            @click="sendMessage"
          >
            <LoadingIcon v-if="sending" />
            <i v-else class="material-icons text-white text-[20px]!">send</i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
